import {
  createAction,
  ForkedTaskAPI,
  isAnyOf,
  ListenerEffectAPI,
  TaskAbortError,
} from "@reduxjs/toolkit";
import { captureException, captureMessage } from "@sentry/react";

import jitsuClient from "@/client/jitsu";
import type { UploadedMedia } from "@/client/media-upload";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { AppStartListening } from "@/data/listenerMiddleware";
import { onOnline } from "@/data/setup-dom-listeners";
import type { AppDispatch, RootState } from "@/data/store";
import { captureRecordingError } from "@/features/socratic/recording-diagnostics";
import { describeUploadFailure } from "@/features/socratic/upload-failure";
import {
  chooseEviction,
  isRetryableUploadError,
  mediaUploaded,
  nextRetryDelay,
  selectNextDrainableTask,
  selectUploadQueue,
  turnCompleted,
  UPLOAD_MEMORY_BUDGET_BYTES,
  uploadDropped,
  uploadFailed,
  uploadProgress,
  uploadQueued,
  type UploadRegistry,
  uploadRestarted,
  uploadRetryScheduled,
  uploadStarted,
  UploadTask,
} from "@/features/socratic/upload-queue-slice";
import { completeSocraticTurn } from "@/features/socratic/upload-queue-slice/api";
import { createUploadHandle } from "@/features/socratic/upload-queue-slice/upload-handle";

type UploadQueueListenerApi = ListenerEffectAPI<RootState, AppDispatch>;

const PROGRESS_INTERVAL_MS = 1000;
const MAX_RESTART_DELAY_MS = 30_000;

/** Start the Video Responses Upload Queue's drain loop. */
export const uploadQueueStarted = createAction("uploadQueue/started");

/**
 * The student asked to restart a stalled upload from zero. This is the only
 * action that throws away the parts already in S3.
 */
export const stalledUploadRetryRequested = createAction<string>(
  "uploadQueue/stalledUploadRetryRequested"
);

/** Every wake is something the student did: recorded, or pressed a button. */
const isWake = isAnyOf(uploadQueued, uploadRestarted);

/**
 * Listener to drive the upload queue of video responses. Only one task is
 * uploaded at a time, oldest first: upload the recording, then complete the
 * turn.
 *
 * The drain loop runs in a fork under a supervisor that re-forks it forever. A
 * fork's promise never rejects, so the supervisor only learns of a crash by
 * awaiting `task.result`. A task's own errors never reach the supervisor: they
 * end in a retry, a drop, or `failed`.
 */
export function startUploadQueueListener(
  startListening: AppStartListening,
  registry: UploadRegistry
) {
  // A second start is a no-op - otherwise we will orphan the Uppy upload in
  // flight.
  let running = false;
  startListening({
    actionCreator: uploadQueueStarted,
    effect: async (_action, listenerApi) => {
      if (running) return;
      running = true;
      try {
        await supervise(listenerApi, registry);
      } finally {
        running = false;
      }
    },
  });

  // When asked to retry
  startListening({
    actionCreator: stalledUploadRetryRequested,
    effect: ({ payload: turnId }, listenerApi) => {
      const entry = registry.lookup(turnId);
      if (entry) {
        entry.handle?.cancel();
        // Keep the recording, drop the dead handle.
        registry.insert(turnId, entry.file);
      }
      listenerApi.dispatch(uploadRestarted(turnId));
    },
  });

  // Listening for memory budget
  let overBudget = false;
  startListening({
    actionCreator: uploadQueued,
    effect: (_action, listenerApi) => {
      for (;;) {
        const decision = chooseEviction(
          selectUploadQueue(listenerApi.getState()),
          registry.byteSize()
        );
        if (decision.kind === "evict") {
          registry.delete(decision.task.turnId);
          listenerApi.dispatch(uploadDropped(decision.task.turnId));
          continue;
        }

        if (decision.kind === "over-budget" && !overBudget) {
          captureMessage("Upload queue is over its memory budget", {
            level: "warning",
            extra: {
              bytes: registry.byteSize(),
              budgetBytes: UPLOAD_MEMORY_BUDGET_BYTES,
              tasks: selectUploadQueue(listenerApi.getState()).length,
            },
          });
        }
        overBudget = decision.kind === "over-budget";
        return;
      }
    },
  });
}

async function supervise(
  listenerApi: UploadQueueListenerApi,
  registry: UploadRegistry
): Promise<void> {
  for (let crashes = 0; ; crashes++) {
    const task = listenerApi.fork((forkApi) =>
      drain(listenerApi, forkApi, registry)
    );
    const result = await task.result;
    if (result.status === "cancelled") return;

    const error =
      result.status === "rejected"
        ? result.error
        : new Error("Upload drain loop exited unexpectedly");
    captureException(error, {
      level: "warning",
      tags: { upload_queue: "restart" },
      extra: {
        crashes,
        tasks: selectUploadQueue(listenerApi.getState()).length,
      },
    });
    await listenerApi.delay(
      Math.min(1000 * 2 ** crashes, MAX_RESTART_DELAY_MS)
    );
  }
}

async function drain(
  listenerApi: UploadQueueListenerApi,
  forkApi: ForkedTaskAPI,
  registry: UploadRegistry
): Promise<never> {
  for (;;) {
    const task = selectNextDrainableTask(listenerApi.getState());
    if (task == null) {
      await forkApi.pause(listenerApi.condition(isWake));
      continue;
    }

    // Around one task, so a task that always throws ends in `failed` instead
    // of crashing the fork on every pass.
    try {
      await driveTask(task, listenerApi, forkApi, registry);
    } catch (error) {
      // The loop is being torn down
      if (error instanceof TaskAbortError) throw error;
      await handleTaskError(task.turnId, error, listenerApi, registry);
    }
  }
}

async function driveTask(
  task: UploadTask,
  listenerApi: UploadQueueListenerApi,
  forkApi: ForkedTaskAPI,
  registry: UploadRegistry
): Promise<void> {
  const { turnId } = task;
  listenerApi.dispatch(uploadStarted(turnId));

  let media = task.media;
  if (!media) {
    media = await forkApi.pause(
      uploadRecording(turnId, listenerApi.dispatch, registry)
    );
    if (!findUploadingTask(listenerApi.getState(), turnId)) return;
    listenerApi.dispatch(mediaUploaded({ turnId, media }));
    registry.delete(turnId);
    // The turn write is the first attempt of its own phase.
    listenerApi.dispatch(uploadStarted(turnId));
  }

  const result = await forkApi.pause(
    completeSocraticTurn({
      workId: task.workId,
      input: {
        turnId,
        mediaId: media.id,
        mediaUrl: media.url,
        durationSeconds: task.durationSeconds,
        recordingStartedAt: task.recordingStartedAt,
        recordingCompletedAt: task.recordingCompletedAt,
      },
    })
  );
  const current = findUploadingTask(listenerApi.getState(), turnId);
  if (!current) return;

  // A rejected turn resolves rather than throws, so no catch would see it.
  const rejection = result.data?.completeSocraticTurn.error;
  if (rejection != null) {
    listenerApi.dispatch(uploadFailed(turnId));
    reportUploadFailure(
      current,
      new Error(`completeSocraticTurn returned ${rejection}`),
      registry
    );
    return;
  }

  listenerApi.dispatch(turnCompleted(turnId));
}

/**
 * The task, if it is still `uploading`. It may have been dropped, or reset by
 * the stall Retry, while the drain loop was awaiting its upload.
 */
function findUploadingTask(
  state: RootState,
  turnId: string
): UploadTask | undefined {
  const task = findTask(state, turnId);
  return task?.status === "uploading" ? task : undefined;
}

/** Drop, retry after the backoff, or fail the task. Never rethrows. */
async function handleTaskError(
  turnId: string,
  error: unknown,
  listenerApi: UploadQueueListenerApi,
  registry: UploadRegistry
): Promise<void> {
  // The error belongs to an attempt the stall Retry already abandoned.
  const task = findUploadingTask(listenerApi.getState(), turnId);
  if (!task) return;

  // Uppy already ran its own part retries, and the student has a newer take.
  if (task.superseded && !task.media) {
    registry.lookup(turnId)?.handle?.cancel();
    registry.delete(turnId);
    listenerApi.dispatch(uploadDropped(turnId));
    return;
  }

  const delayMs = isRetryableUploadError(error)
    ? nextRetryDelay(task.attempts)
    : null;
  if (delayMs === null) {
    listenerApi.dispatch(uploadFailed(turnId));
    reportUploadFailure(task, error, registry);
    return;
  }

  listenerApi.dispatch(uploadRetryScheduled(turnId));
  // Reconnecting only shortens the sleep; the timeout still fires on its own.
  await listenerApi.condition(onOnline.match, delayMs);
}

/** Report a task reaching `failed`, once, however many attempts it made. */
function reportUploadFailure(
  task: UploadTask,
  error: unknown,
  registry: UploadRegistry
): void {
  const uploadSucceeded = task.media != null;
  const failure = describeUploadFailure(error, { uploadSucceeded });
  const file = registry.lookup(task.turnId)?.file;
  const context = {
    dialogue_id: task.dialogueId,
    turn_id: task.turnId,
    attempts: task.attempts,
    superseded: task.superseded,
    online: navigator.onLine,
    file_size: file?.size ?? task.media?.file_size,
    duration_seconds: task.durationSeconds,
    mime: file?.type,
    ...failure,
  };

  jitsuClient.track(JitsuEvent.VIDEO_RESPONSE_FAILED, {
    error: uploadSucceeded ? "complete_turn_failed" : "upload_failed",
    error_message: error instanceof Error ? error.message : String(error),
    ...context,
  });
  captureRecordingError(
    error,
    { work_id: task.workId, reason: failure.reason },
    context
  );
}

function findTask(state: RootState, turnId: string): UploadTask | undefined {
  return selectUploadQueue(state).find((task) => task.turnId === turnId);
}

async function uploadRecording(
  turnId: string,
  dispatch: AppDispatch,
  registry: UploadRegistry
): Promise<UploadedMedia> {
  const entry = registry.lookup(turnId);
  if (!entry) throw new Error("No recording in the upload registry");
  if (entry.handle) return entry.handle.resume();

  const handle = createUploadHandle(entry.file, {
    onProgress: progressReporter(turnId, dispatch),
  });
  registry.attachHandle(turnId, handle);
  return handle.start();
}

/** At most one progress action a second; Uppy reports far more often. */
function progressReporter(turnId: string, dispatch: AppDispatch) {
  let lastAt = 0;
  return (percent: number) => {
    const now = Date.now();
    if (now - lastAt < PROGRESS_INTERVAL_MS) return;
    lastAt = now;
    dispatch(uploadProgress({ turnId, percent }));
  };
}
